/**
 * Unified loader for all splat file formats using splat-transform.
 */

import {
    getInputFormat,
    readFile,
    sortMortonOrder,
    Column,
    ColumnType,
    DataTable,
    Options,
    ReadFileSystem,
    Transform,
    ZipReadFileSystem
} from '@playcanvas/splat-transform';
import { GSplatData, Quat } from 'playcanvas';

type LoadedSplatAsset =
    | { kind: 'gsplat-data'; gsplatData: GSplatData; transform: any }
    | { kind: 'streamed-lod'; manifestData: any; estimatedSplatCount: number };

type LoadedGSplatData = {
    gsplatData: GSplatData;
    transform: any;
};

/**
 * Default options for readFile.
 */
const defaultOptions: Options = {
    iterations: 10,
    lodSelect: [0],
    unbundled: false,
    lodChunkCount: 512,
    lodChunkExtent: 16
};

/**
 * Map splat-transform column types to GSplatData property types.
 */
const columnTypeToGSplatType = (colType: ColumnType | null): string => {
    switch (colType) {
        case 'int8': return 'char';
        case 'uint8': return 'uchar';
        case 'int16': return 'short';
        case 'uint16': return 'ushort';
        case 'int32': return 'int';
        case 'uint32': return 'uint';
        case 'float32': return 'float';
        case 'float64': return 'double';
        default: return 'float';
    }
};

/**
 * Convert a splat-transform DataTable to PlayCanvas GSplatData.
 */
const dataTableToGSplatData = (dataTable: DataTable): GSplatData => {
    const properties = dataTable.columns.map((col: Column) => ({
        type: columnTypeToGSplatType(col.dataType),
        name: col.name,
        storage: col.data,
        byteSize: col.data.BYTES_PER_ELEMENT
    }));

    const gsplatData = new GSplatData([{
        name: 'vertex',
        count: dataTable.numRows,
        properties
    }]);

    // Support loading 2D splats by adding scale_2 property with almost 0 scale
    if (gsplatData.getProp('scale_0') && gsplatData.getProp('scale_1') && !gsplatData.getProp('scale_2')) {
        const scale2 = new Float32Array(gsplatData.numSplats).fill(Math.log(1e-6));
        gsplatData.addProp('scale_2', scale2);

        // Place the new scale_2 property just after scale_1
        const props = gsplatData.getElement('vertex').properties;
        props.splice(props.findIndex((prop: any) => prop.name === 'scale_1') + 1, 0, props.splice(props.length - 1, 1)[0]);
    }

    return gsplatData;
};

const dataTableToLoadedGSplatData = (dataTable: DataTable, transform?: Transform | Readonly<Transform> | null): LoadedGSplatData => {
    return {
        gsplatData: dataTableToGSplatData(dataTable),
        transform: transform?.clone?.() ?? dataTable.transform?.clone?.() ?? null
    };
};

const numberTuple = (value: unknown, length: number): number[] | null => {
    if (!Array.isArray(value) || value.length !== length || value.some(v => typeof v !== 'number' || !Number.isFinite(v))) {
        return null;
    }
    return value as number[];
};

const transformFromQuat = (value: unknown) => {
    const tuple = numberTuple(value, 4);
    if (!tuple) {
        return null;
    }

    const rotation = new Quat(tuple[0], tuple[1], tuple[2], tuple[3]).normalize();
    return new Transform(undefined, rotation);
};

const transformFromEuler = (value: unknown) => {
    const tuple = numberTuple(value, 3);
    return tuple ? new Transform().fromEulers(tuple[0], tuple[1], tuple[2]) : null;
};

const normalizeCoordinateHint = (value: unknown) => {
    return typeof value === 'string' ? value.toLowerCase().replace(/[^a-z0-9+]/g, '') : '';
};

const transformFromCoordinateHint = (value: unknown) => {
    const hint = normalizeCoordinateHint(value);
    if (!hint) {
        return null;
    }

    if (['identity', 'engine', 'engineyup', 'playcanvas', 'playcanvasyup', 'supersplat', 'supersplatyup', 'nsite', 'nsiteyup', 'yup', '+yup', 'yaxisup'].includes(hint)) {
        return Transform.IDENTITY.clone();
    }

    if (['ply', 'plyspace', 'legacy', 'legacysog', 'legacyply', 'colmap'].includes(hint)) {
        return Transform.PLY.clone();
    }

    if (['zup', '+zup', 'zaxisup'].includes(hint)) {
        return new Transform().fromEulers(-90, 0, 0);
    }

    return null;
};

const getSogTransformOverride = (meta: any) => {
    const transformMeta = meta?.transform ?? meta?.asset?.transform ?? meta?.coordinateTransform ?? meta?.asset?.coordinateTransform;
    const explicitTransform =
        transformFromQuat(transformMeta?.rotation) ??
        transformFromQuat(transformMeta?.quaternion) ??
        transformFromQuat(transformMeta?.rotationQuaternion) ??
        transformFromEuler(transformMeta?.euler) ??
        transformFromEuler(transformMeta?.eulerDegrees) ??
        transformFromEuler(transformMeta?.rotationEuler) ??
        transformFromEuler(transformMeta?.rotationDegrees);

    if (explicitTransform) {
        return explicitTransform;
    }

    return [
        meta?.coordinateSystem,
        meta?.sourceCoordinateSystem,
        meta?.sourceSpace,
        meta?.upAxis,
        meta?.asset?.coordinateSystem,
        meta?.asset?.sourceCoordinateSystem,
        meta?.asset?.sourceSpace,
        meta?.asset?.upAxis,
        transformMeta?.coordinateSystem,
        transformMeta?.sourceCoordinateSystem,
        transformMeta?.sourceSpace,
        transformMeta?.upAxis
    ].reduce((result, hint) => result ?? transformFromCoordinateHint(hint), null as Transform | null);
};

const isStreamedLodFilename = (filename: string) => {
    const normalized = filename.replace(/\\/g, '/').toLowerCase();
    return normalized === 'lod-meta.json' || normalized.endsWith('/lod-meta.json');
};

const estimateStreamedLodSplatCount = (node: any): number => {
    if (!node || typeof node !== 'object') {
        return 0;
    }

    if (node.lods && typeof node.lods === 'object') {
        return (Object.values(node.lods) as any[]).reduce((maxCount: number, lod: any) => {
            const count = typeof lod?.count === 'number' ? lod.count : 0;
            return Math.max(maxCount, count);
        }, 0);
    }

    if (Array.isArray(node.children)) {
        return node.children.reduce((sum: number, child: any) => sum + estimateStreamedLodSplatCount(child), 0);
    }

    return 0;
};

const readJsonFromFileSystem = async (filename: string, fileSystem: ReadFileSystem) => {
    const source = await fileSystem.createSource(filename);
    try {
        const data = await source.read().readAll();
        return JSON.parse(new TextDecoder().decode(data));
    } finally {
        source.close();
    }
};

const readSogTransformOverride = async (filename: string, fileSystem: ReadFileSystem) => {
    try {
        return getSogTransformOverride(await readJsonFromFileSystem(filename, fileSystem));
    } catch (error) {
        return null;
    }
};

/**
 * Load a file using splat-transform and convert to GSplatData.
 * @param filename - The filename to load
 * @param fileSystem - The file system to read from
 * @param skipReorder - Skip morton reordering (for files already in morton order or animation playback)
 */
const loadGSplatDataWithTransform = async (filename: string, fileSystem: ReadFileSystem, skipReorder?: boolean): Promise<LoadedGSplatData> => {
    const inputFormat = getInputFormat(filename);
    const lowerFilename = filename.toLowerCase();

    // Handle bundled SOG (.sog extension) - wrap with ZipReadFileSystem
    if (inputFormat === 'sog' && lowerFilename.endsWith('.sog')) {
        const source = await fileSystem.createSource(filename);
        const zipFs = new ZipReadFileSystem(source);
        try {
            const transformOverride = await readSogTransformOverride('meta.json', zipFs);
            const tables = await readFile({
                filename: 'meta.json',
                inputFormat: 'sog',
                options: defaultOptions,
                params: [],
                fileSystem: zipFs
            });
            return dataTableToLoadedGSplatData(tables[0], transformOverride);
        } finally {
            zipFs.close();
        }
    }

    const transformOverride = inputFormat === 'sog' ? await readSogTransformOverride(filename, fileSystem) : null;

    // Read the file using splat-transform
    const tables = await readFile({
        filename,
        inputFormat,
        options: defaultOptions,
        params: [],
        fileSystem
    });

    // Reorder data into morton order for better render performance.
    // Skip reordering for:
    // - SOG format (already in morton order)
    // - Compressed PLY (already in morton order from write-compressed-ply)
    // - When skipReorder is true (ssproj files are already ordered, animation frames need speed)
    const isCompressedPly = lowerFilename.endsWith('.compressed.ply');
    if (inputFormat !== 'sog' && !isCompressedPly && !skipReorder) {
        const indices = new Uint32Array(tables[0].numRows);
        for (let i = 0; i < indices.length; i++) {
            indices[i] = i;
        }
        sortMortonOrder(tables[0], indices);
        tables[0].permuteRowsInPlace(indices);
    }

    // Convert to GSplatData (use first table, as most formats return single table)
    // LCC may return multiple tables for different LOD levels - we use the first (highest detail)
    return dataTableToLoadedGSplatData(tables[0], transformOverride);
};

const loadGSplatData = async (filename: string, fileSystem: ReadFileSystem, skipReorder?: boolean): Promise<GSplatData> => {
    return (await loadGSplatDataWithTransform(filename, fileSystem, skipReorder)).gsplatData;
};

const loadSplatAsset = async (filename: string, fileSystem: ReadFileSystem, skipReorder?: boolean): Promise<LoadedSplatAsset> => {
    if (isStreamedLodFilename(filename)) {
        const manifestData = await readJsonFromFileSystem(filename, fileSystem);
        return {
            kind: 'streamed-lod',
            manifestData,
            estimatedSplatCount: estimateStreamedLodSplatCount(manifestData?.tree)
        };
    }

    return {
        kind: 'gsplat-data',
        ...await loadGSplatDataWithTransform(filename, fileSystem, skipReorder)
    };
};

/**
 * Validate that GSplatData contains required properties.
 */
const validateGSplatData = (gsplatData: GSplatData): void => {
    const required = [
        'x', 'y', 'z',
        'scale_0', 'scale_1', 'scale_2',
        'rot_0', 'rot_1', 'rot_2', 'rot_3',
        'f_dc_0', 'f_dc_1', 'f_dc_2', 'opacity'
    ];

    const missing = required.filter(x => !gsplatData.getProp(x));
    if (missing.length > 0) {
        throw new Error(`This file does not contain gaussian splatting data. The following properties are missing: ${missing.join(', ')}`);
    }
};

export {
    isStreamedLodFilename,
    loadSplatAsset,
    loadGSplatData,
    loadGSplatDataWithTransform,
    validateGSplatData
};

export type {
    LoadedSplatAsset
};
